/**
 * After a report is marked Fixed: offer to say so in its own thread.
 *
 * The checkmark already went on the post — that happens server-side the moment the status is
 * saved, whether this dialog is used or not. What the checkmark cannot do is say WHAT changed,
 * and the people who filed these reports were being told nothing. So: one suggested note, in the
 * thread the report came from, which a person reads before it goes.
 *
 * ⚠️ Skip is a real answer, and it is the one on the left. A dialog whose only exit is "post"
 * turns a suggestion into a step, and the two "Test" posts in that channel want no reply at all.
 */
import { useEffect, useRef, useState } from 'react';
import { MessageSquare, X } from 'lucide-react';
import { Button } from '@finn-auto/design-system/atoms/button';
import type { FeedbackReport } from '../../services/feedbackTriageApi';
import { RADIUS } from './triageTokens';
import { buildResolutionDraft } from './resolutionDraft';

interface Props {
  report: FeedbackReport;
  posting: boolean;
  error: string | null;
  onPost: (text: string) => void;
  onSkip: () => void;
}

export function ResolveDialog({ report, posting, error, onPost, onSkip }: Props) {
  const [text, setText] = useState(() => buildResolutionDraft(report));
  const areaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    areaRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !posting) onSkip();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onSkip, posting]);

  const noDecision = !report.decision.trim();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Skip the note"
        className="absolute inset-0 bg-zinc-900/30"
        onClick={() => !posting && onSkip()}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="resolve-title"
        className={`relative flex w-[min(620px,100%)] flex-col gap-4 border border-zinc-200 bg-white p-6 shadow-xl ${RADIUS}`}
      >
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
            <MessageSquare className="size-4" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id="resolve-title" className="text-base font-bold text-zinc-900">
              Let them know it is fixed?
            </h2>
            <p className="mt-1 text-[13px] text-zinc-500">
              The ✅ is already on the post. This goes into that report&apos;s thread, where the
              person who reported it will see it.
            </p>
          </div>
          <button
            type="button"
            onClick={onSkip}
            disabled={posting}
            aria-label="Skip"
            className={`inline-flex size-7 items-center justify-center ${RADIUS} border border-zinc-200 text-zinc-500 hover:bg-zinc-50 hover:text-zinc-900`}
          >
            <X className="size-3.5" aria-hidden />
          </button>
        </div>

        <p className="truncate text-[12.5px] text-zinc-500" title={report.title}>
          Replying to: <span className="font-semibold text-zinc-700">{report.title}</span>
        </p>

        {/* Nothing was typed in the decision field, so the draft is one generic line. Say so
            rather than letting a thin note go out looking considered. */}
        {noDecision && (
          <p className={`border border-amber-200 bg-amber-50 p-2.5 text-[12.5px] text-amber-800 ${RADIUS}`}>
            No decision was written on this one, so the note says only that it is fixed. A line
            about what changed is the part they actually want.
          </p>
        )}

        <label className="sr-only" htmlFor="resolve-text">
          The note to post in the thread
        </label>
        <textarea
          id="resolve-text"
          ref={areaRef}
          rows={7}
          value={text}
          disabled={posting}
          onChange={(e) => setText(e.target.value)}
          className={`w-full resize-y ${RADIUS} border border-zinc-200 bg-white px-3 py-2.5 text-[13px] leading-relaxed text-zinc-900 focus:outline-none focus:ring-2 focus:ring-blue-500`}
        />

        {error && (
          <p className={`border border-red-200 bg-red-50 p-2.5 text-[12.5px] text-red-800 ${RADIUS}`}>
            {error}
          </p>
        )}

        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={onSkip} disabled={posting}>
            Skip
          </Button>
          <div className="flex-1" />
          <Button variant="primary" onClick={() => onPost(text)} disabled={posting || !text.trim()}>
            {posting ? 'Posting…' : 'Post in the thread'}
          </Button>
        </div>
      </div>
    </div>
  );
}
